import type { AnalyticsGeographicLevel } from './analyticsTypes'

const geographicDepthOrder: AnalyticsGeographicLevel[] = [
  'country',
  'macroRegion',
  'state',
  'mesoRegion',
  'microRegion',
  'city',
  'neighborhood',
  'street',
  'custom',
  'item',
]

export interface AnalyticsMapTrackingSession {
  layersUsed: Set<string>
  mapTypesUsed: Set<string>
  maximumZoomLevel: number | null
  minimumGeographicLevelReached: string | null
  regionsSelectedCount: number
  startedAt: number
  totalMapMovements: number
}

export function createAnalyticsMapTrackingSession(): AnalyticsMapTrackingSession {
  return {
    layersUsed: new Set(),
    mapTypesUsed: new Set(),
    maximumZoomLevel: null,
    minimumGeographicLevelReached: null,
    regionsSelectedCount: 0,
    startedAt: Date.now(),
    totalMapMovements: 0,
  }
}

function resolveGeographicDepth(geographicLevel: string | null | undefined) {
  const index = geographicDepthOrder.indexOf(geographicLevel as AnalyticsGeographicLevel)
  return index === -1 ? -1 : index
}

export function recordMapMovement(session: AnalyticsMapTrackingSession, zoomLevel?: number | null) {
  session.totalMapMovements += 1

  if (typeof zoomLevel === 'number' && Number.isFinite(zoomLevel)) {
    const roundedZoomLevel = Math.round(zoomLevel)
    session.maximumZoomLevel =
      session.maximumZoomLevel === null
        ? roundedZoomLevel
        : Math.max(session.maximumZoomLevel, roundedZoomLevel)
  }
}

export function recordMapTypeUsed(session: AnalyticsMapTrackingSession, mapType?: string | null) {
  if (mapType) {
    session.mapTypesUsed.add(mapType)
  }
}

export function recordMapLayerUsed(
  session: AnalyticsMapTrackingSession,
  layerIdentifier?: string | null,
) {
  if (layerIdentifier) {
    session.layersUsed.add(layerIdentifier)
  }
}

export function recordMapRegionSelected(
  session: AnalyticsMapTrackingSession,
  geographicLevel?: string | null,
) {
  if (!geographicLevel) {
    return
  }

  session.regionsSelectedCount += 1

  const currentDepth = resolveGeographicDepth(session.minimumGeographicLevelReached)
  const nextDepth = resolveGeographicDepth(geographicLevel)
  if (nextDepth > currentDepth) {
    session.minimumGeographicLevelReached = geographicLevel
  }
}

export function buildMapExplorationSummary(session: AnalyticsMapTrackingSession) {
  const sessionDurationSeconds = Math.max(0, Math.round((Date.now() - session.startedAt) / 1000))

  return {
    layers_used_count: session.layersUsed.size,
    map_types_used: Array.from(session.mapTypesUsed),
    maximum_zoom_level: session.maximumZoomLevel ?? undefined,
    minimum_geographic_level_reached: session.minimumGeographicLevelReached ?? undefined,
    regions_selected_count: session.regionsSelectedCount,
    session_duration_seconds: sessionDurationSeconds,
    total_map_movements: session.totalMapMovements,
  }
}
